import { useCallback, useEffect, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import { RadioInput } from 'components/KnowledgeTest/Styled';
import { HRSeparator, SeparatorText, SeparatorWrapper } from './Styled';
import LampIcon from 'images/reliabilityTest/lampIcon.svg';
import { CertificationItem } from 'types/api/CertificationItemProductKey';
import {
  formatCertification,
  getVersions,
  getAgeLevelGroup,
  sortByAgeLevelsOrder,
  sortByAnchorAgeLevelGroup,
  isMultipleCertifications,
  getCertificationKey,
  getCertificationSignature
} from './utils';
import InfoPopover from './InfoPopover';

interface ChooseCertificationTypeProps {
  certifications: {
    recertification: CertificationItem[];
    transition: CertificationItem[];
  };
  setSelectedCertification: (certification: CertificationItem) => void;
  testTemplateId: number;
  role: string;
  selectedType: 'recertification' | 'transition';
  bundledUserCertificationIds?: number[];
}

function ChooseCertificationType({
  certifications,
  setSelectedCertification,
  testTemplateId,
  role,
  selectedType,
  bundledUserCertificationIds
}: ChooseCertificationTypeProps) {
  const [uniqueRecertifications, setUniqueRecertifications] = useState<
    CertificationItem[]
  >([]);
  const [uniqueTransitions, setUniqueTransitions] = useState<
    CertificationItem[]
  >([]);
  const [recertificationData, setRecertificationData] = useState<
    CertificationItem[]
  >([]);
  const [transitionData, setTransitionData] = useState<CertificationItem[]>([]);

  const selectedKey = getCertificationKey(
    {
      test_template_id: testTemplateId,
      role,
      bundled_user_certification_ids: bundledUserCertificationIds
    },
    selectedType
  );

  const selectCertification = useCallback(
    (
      certification: CertificationItem,
      type: 'recertification' | 'transition'
    ) => {
      if (certification.disabled) return;
      setSelectedCertification({
        ...certification,
        type,
        multiple_certifications: isMultipleCertifications(certification)
      });
    },
    [setSelectedCertification]
  );

  function isThereOnlyOneRecertification(
    recertifications: CertificationItem[],
    transitions: CertificationItem[]
  ) {
    return recertifications.length === 1 && transitions.length === 0;
  }

  function isThereOnlyOneTransition(
    recertifications: CertificationItem[],
    transitions: CertificationItem[]
  ) {
    return transitions.length === 1 && recertifications.length === 0;
  }

  const selectCertificationByDefault = useCallback(
    (
      recertifications: CertificationItem[],
      transitions: CertificationItem[]
    ) => {
      let selectedCertification: CertificationItem[] = [];
      let certificationType = '';
      if (isThereOnlyOneRecertification(recertifications, transitions)) {
        certificationType = 'recertification';
        selectedCertification = recertifications;
      } else if (isThereOnlyOneTransition(recertifications, transitions)) {
        certificationType = 'transition';
        selectedCertification = transitions;
      }

      if (
        (certificationType === 'recertification' ||
          certificationType === 'transition') &&
        !selectedCertification[0].disabled
      ) {
        selectCertification(
          {
            ...selectedCertification[0],
            age_level_group: getAgeLevelGroup(selectedCertification[0].name),
            version: getVersions(selectedCertification[0].name)
          },
          certificationType
        );
      }
    },
    [selectCertification]
  );

  useEffect(() => {
    const dedupRecertifications = [...certifications.recertification]
      .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled))
      .filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            t =>
              getCertificationSignature(t) === getCertificationSignature(value)
          )
      );

    const dedupTransitions = [...certifications.transition]
      .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled))
      .filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            t =>
              getCertificationSignature(t) === getCertificationSignature(value)
          )
      );

    selectCertificationByDefault(dedupRecertifications, dedupTransitions);
    setUniqueRecertifications(dedupRecertifications);
    setUniqueTransitions(dedupTransitions);
  }, [certifications, selectCertificationByDefault]);

  useEffect(() => {
    const recertificationsOnly = uniqueRecertifications.map(
      (item: CertificationItem) => ({
        ...item,
        version: getVersions(item.name),
        name: item.name,
        age_level_group: getAgeLevelGroup(item.name),
        multiple_certifications: isMultipleCertifications(item)
      })
    );

    const recertificationsToOrder = [...recertificationsOnly];
    recertificationsToOrder.sort(sortByAnchorAgeLevelGroup);
    setRecertificationData(recertificationsToOrder);
  }, [uniqueRecertifications]);

  useEffect(() => {
    const transitionsOnly = uniqueTransitions.map(
      (item: CertificationItem) => ({
        ...item,
        version: getVersions(item.name),
        name: item.name,
        age_level_group: getAgeLevelGroup(item.name),
        eligible_for_transition: item.eligible_for_transition,
        multiple_certifications: isMultipleCertifications(item)
      })
    );

    const transitionsToOrder = [...transitionsOnly];
    transitionsToOrder.sort(sortByAgeLevelsOrder);
    setTransitionData(transitionsToOrder);
  }, [uniqueTransitions]);

  return (
    <div className="rounded-t-sm bg-white p-4 my-4">
      <div className="my-4 flex items-center">
        <div className="text-xl font-bold mr-4">{i18n.ft(messages.title)}</div>
        <InfoPopover
          label={i18n.ft(messages.title)}
          content={i18n.ft(messages.select)}
        />
      </div>
      <SeparatorWrapper>
        <div className="my-4">
          {transitionData?.map((certification: CertificationItem) => {
            const certificationKey = getCertificationKey(
              certification,
              'transition'
            );
            const isChecked = certificationKey === selectedKey;

            return (
              <div key={certificationKey}>
                <div
                  className={`grid grid-cols-9 py-1 mt-4 ${
                    isChecked
                      ? 'bg-[#EDEFF3] border-[#4A8264] border-solid border-2'
                      : 'bg-[#F3F4F6]'
                  } rounded-lg cursor-pointer transition-colors hover:bg-[#EDEFF3] hover:shadow-lg`}
                  onClick={() => {
                    selectCertification(certification, 'transition');
                  }}
                >
                  <div className="col-span-9 md:col-span-6 relative text-left pl-4">
                    <div className="grid grid-cols-6 gap-1 items-center">
                      <div className="col-span-1">
                        <RadioInput
                          id={`question_2${certificationKey}`}
                          name="certification_option"
                          data-answer-id={certification.test_template_id}
                          onChange={() => {
                            selectCertification(certification, 'transition');
                          }}
                          checked={isChecked}
                          disabled={!!certification.disabled}
                          error={false}
                        />
                      </div>
                      <div className="col-span-5">
                        <label
                          htmlFor={`question_2${certificationKey}`}
                          className="px-5 cursor-pointer"
                        >
                          <div className="text-lg">
                            {formatCertification(certification)}
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-9 md:col-span-3 relative self-center">
                    <div className="grid grid-cols-6 gap-1 block md:hidden">
                      <div className="col-span-1"></div>
                      <div className="col-span-5">
                        <hr className="border-[#DEDEDE] w-44 border-b-2 rounded" />
                        <div className="text-left py-4 pl-3">
                          {i18n.ft(messages.transition)}
                        </div>
                      </div>
                    </div>
                    <div className="hidden text-center p-4 md:block">
                      {i18n.ft(messages.transition)}
                    </div>
                    <div className="absolute inset-y-0 left-0 w-px bg-[#DEDEDE]"></div>
                  </div>
                </div>
                {certification.eligible_for_transition && (
                  <div className="grid grid-cols-12 text-sm text-left px-1.5">
                    <div className="col-span-1">
                      <img src={LampIcon} alt="lamp" />
                    </div>
                    <div className="col-span-11">
                      <div>{i18n.ft(messages.note)}</div>
                      <a href="/">{i18n.ft(messages.learn)}</a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {recertificationData.length > 0 && transitionData.length > 0 ? (
          <div className="my-4 py-4">
            <HRSeparator />
            <SeparatorText>{i18n.ft(messages.or)}</SeparatorText>
          </div>
        ) : null}

        <div className="my-4">
          {recertificationData?.map(
            (certification: CertificationItem, index: number) => {
              const certificationKey = getCertificationKey(
                certification,
                'recertification'
              );
              const isChecked = certificationKey === selectedKey;
              const showGroupHeader =
                certification.anchor_age_level_group &&
                certification.anchor_age_level_group !==
                  recertificationData[index - 1]?.anchor_age_level_group;

              return (
                <div
                  key={certificationKey}
                  title={certification.disabled_reason}
                >
                  {showGroupHeader && (
                    <div className="text-lg font-bold mt-6 first:mt-0">
                      {certification.anchor_age_level_group}
                    </div>
                  )}
                  <div
                    className={`grid grid-cols-9 py-1 mt-4 ${
                      isChecked
                        ? 'bg-[#EDEFF3] border-[#4A8264] border-solid border-2'
                        : 'bg-[#F3F4F6]'
                    } rounded-lg cursor-pointer transition-colors hover:bg-[#EDEFF3] hover:shadow-lg`}
                    onClick={() => {
                      if (certification.disabled) return;
                      selectCertification(certification, 'recertification');
                    }}
                  >
                    <div className="col-span-9 md:col-span-6 relative text-left pl-4">
                      <div className="grid grid-cols-6 gap-1 items-center">
                        <div className="col-span-1">
                          <RadioInput
                            id={`question_${certificationKey}`}
                            name="certification_option"
                            data-answer-id={certification.test_template_id}
                            onChange={() => {
                              selectCertification(
                                certification,
                                'recertification'
                              );
                            }}
                            checked={isChecked}
                            error={false}
                            disabled={certification.disabled || false}
                          />
                        </div>
                        <div className="col-span-5">
                          <label
                            htmlFor={`question_${certificationKey}`}
                            className="px-5 cursor-pointer"
                          >
                            <div className="text-lg">
                              {formatCertification(certification)}
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-9 md:col-span-3 relative self-center">
                      <div className="grid grid-cols-6 gap-1 block md:hidden">
                        <div className="col-span-1"></div>
                        <div className="col-span-5">
                          <hr className="border-[#DEDEDE] w-44 border-b-2 rounded" />
                          <div className="text-left py-4 pl-3">
                            {i18n.ft(messages.recertify)}
                          </div>
                        </div>
                      </div>
                      <div className="hidden text-center py-4 md:block">
                        {i18n.ft(messages.recertify)}
                      </div>
                      <div className="absolute inset-y-0 left-0 w-px bg-[#DEDEDE]"></div>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </SeparatorWrapper>

      <div className="flex justify-center">
        <p>{i18n.ft(messages.exceededAttempts)}</p>
      </div>
    </div>
  );
}

export default ChooseCertificationType;
